import React, {useState} from 'react';
import {useLocation} from "react-router-dom";
import ReactModal from "react-modal";
import { useForm } from 'react-hook-form';
import useLoading from '../../../../hooks/useLoading';
import { roomSalesmanApi } from '../../../../apis/admin/room/roomSalesmanApi';
import {Salesman} from '../../../../types/admin/room';
import {ApiClientError} from "../../../../apis/errors";
import useAppRouter from "../../../../hooks/useAppRouter";
import {ValidationErrorMessages} from "../../common/ValidationErrorMessages";
import {FormValidationType, makeRegisterOptions} from "../../../../libs/reactHookForm/makeRegisterOptions";
import {logError} from "../../../../libs/utils/logError";


interface FormValuesOfSearchSalesman {
  searchEmail: string;
}

interface SelectSalesmanModalProps {
  selectedSalesmanEmail: string[];
  onSubmitted: (isChange: boolean, salesman: Salesman) => Promise<void>;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
}

interface FormValuesOfCreateUser {
  userName: string;
  companyName: string;
  email: string;
  department: string;
  employeeNumber: string;
}


export const SelectSalesmanModal = (props: SelectSalesmanModalProps) => {
  const { selectedSalesmanEmail, onSubmitted, setShowModal } = props;

  const withLoading = useLoading();
  const location = useLocation();

  const {
    register: searchFormRegister,
    setError: searchFormSetError,
    formState: { errors: searchFormErrors, },
    handleSubmit: searchFormHandleSubmit
  } = useForm({
    reValidateMode: 'onSubmit',
  });

  const {
    register: createUserRegister,
    formState: { errors: createUserErrors, },
    handleSubmit: createUserHandleSubmit,
    setValue: createUserSetValue,
  } = useForm({
    reValidateMode: 'onSubmit',
  });

  const [showSalesmanSearch, setShowSalesmanSearch] = React.useState<boolean>(true);
  const [showSalesmanResult, setShowSalesmanResult] = React.useState<boolean>(false);
  const [showSalesmanResultNone, setShowSalesmanResultNone] = React.useState<boolean>(false);

  const [salesmanFoundBySearch, setSalesmanFoundBySearch] = React.useState<Salesman>();

  const [ apiClientError, setApiClientError ] = useState<ApiClientError | null>(null);
  const { goto } = useAppRouter();

  const handleOwnerEmailSubmit = async () => {
    withLoading(async () => {
      setApiClientError(null);

      await searchFormHandleSubmit(async (data: FormValuesOfSearchSalesman) => {
        try {
          const isSelectedSalesmanEmail = selectedSalesmanEmail.some(email => {
            return email === data.searchEmail
          });
          if (isSelectedSalesmanEmail) {
            searchFormSetError('searchEmail',
              {
                type: 'manual',
                message: 'このメールアドレスの営業ユーザーは選択済みです',
              });
            return;
          }

          const response = await roomSalesmanApi.search({email: data.searchEmail}, {});

          setShowSalesmanSearch(false);
          if (response.isExist) {
            setSalesmanFoundBySearch(response.salesman);
            setShowSalesmanResult(true);
          } else {
            setShowSalesmanResultNone(true);
            createUserSetValue('email', data.searchEmail);
          }

        } catch (e) {
          await logError(
            location.pathname,
            '営業ユーザーの検索に失敗しました。',
            e
          );

          if (e instanceof ApiClientError) {
            setApiClientError(e);
            return;
          }

          goto('internalServerError');
        }
      })();
    });
  };

  const handleUserInfoChange = async (data: FormValuesOfCreateUser) => {
    withLoading(async () => {
      setApiClientError(null);

      try {
        const response = await roomSalesmanApi.create(data, {});

        await onSubmitted(true, {
          userId: response.salesman.userId,
          userName: response.salesman.userName,
          email: response.salesman.email,
          companyName: response.salesman.companyName,
          department: response.salesman.department,
          employeeNumber: response.salesman.employeeNumber,
        })

      } catch (e) {
        await logError(
          location.pathname,
          '営業ユーザーの作成に失敗しました。',
          e
        );

        if (e instanceof ApiClientError) {
          setApiClientError(e);
          return;
        }

        goto('internalServerError');
      }

    });
  };

  const add = () => {
    if (salesmanFoundBySearch === undefined) {
      return;
    }

    onSubmitted(true, {
      userId: salesmanFoundBySearch.userId,
      userName: salesmanFoundBySearch.userName,
      email: salesmanFoundBySearch.email,
      companyName: salesmanFoundBySearch.companyName,
      department: salesmanFoundBySearch.department,
      employeeNumber: salesmanFoundBySearch.employeeNumber,
    })
  }

  return (
    <>
      <ReactModal
        isOpen
        ariaHideApp={false}
        onRequestClose={() => setShowModal(false)}
        overlayClassName="overlay"
        shouldCloseOnOverlayClick
        className="form-modal"
      >
        <section className="modal-background">
          <div className="form-modal-container">
            <div className="form-modal-title">
              <h1 className="font-semibold">営業追加</h1>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>

            { showSalesmanSearch && (
              <form id="searchUserForm">
                <div className='form-modal-content'>
                  <div className="text-danger text-xs mb-4">
                    { apiClientError?.message }
                    { (Object.keys(searchFormErrors).length > 0) && '入力エラーがあります' }
                  </div>

                  <div className="grid grid-cols-1 w-full">
                    <label className="block" htmlFor="searchEmail">
                      <input
                        /* eslint-disable-next-line react/jsx-props-no-spreading */
                        {...searchFormRegister('searchEmail',
                          makeRegisterOptions('メールアドレス', [
                            { type: FormValidationType.REQUIRED },
                            { type: FormValidationType.MAX_STRING_LENGTH, value: 86 },
                            { type: FormValidationType.EMAIL, typeName: 'email' },
                          ])
                        )}
                        className="text-field"
                        id="searchEmail"
                        type="text"
                        placeholder="検索する営業のメールアドレスを入力してください"
                        maxLength={86}
                      />
                      <div className="mt-1 text-danger text-xs">
                        {searchFormErrors.searchEmail?.message}
                      </div>
                      <ValidationErrorMessages
                        {...{
                          fieldErrors: apiClientError?.errors,
                          field: 'email',
                        }}
                      />
                    </label>
                  </div>
                </div>
              </form>
            )}

            { showSalesmanResult && (
              <div className='form-modal-content'>
                <div className="grid grid-cols-1 w-full">
                  <span className="text-sm font-semibold text-primary mb-4">下記のユーザがヒットしました。データを確認して追加してください。</span>
                  <table className="w-full table-fixed mt-2">
                    <tbody className="text-sm">
                      <tr>
                        <td>名前</td>
                        <td>{ salesmanFoundBySearch?.userName }</td>
                      </tr>
                      <tr>
                        <td>部署名</td>
                        <td>{ salesmanFoundBySearch?.department }</td>
                      </tr>
                      <tr>
                        <td>E-mail</td>
                        <td>{ salesmanFoundBySearch?.email }</td>
                      </tr>
                      <tr>
                        <td>社員番号</td>
                        <td>{ salesmanFoundBySearch?.employeeNumber }</td>
                      </tr>
                      <tr>
                        <td>会社名</td>
                        <td>{ salesmanFoundBySearch?.companyName }</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {showSalesmanResultNone && (
              <form onSubmit={createUserHandleSubmit(handleUserInfoChange)} id="createUserForm">
                <div className="form-modal-content overflow-y-auto" style={{ flexDirection: 'unset', height: '30rem' }}>
                  <div className="grid grid-cols-1 w-full">
                    <span className="text-sm font-semibold text-primary mb-2">未登録の営業です。データを新規に登録してください。</span>
                    <div className="text-danger text-xs mb-4">
                      { apiClientError?.message }
                      { (Object.keys(createUserErrors).length > 0) && '入力エラーがあります' }
                    </div>

                    <label className="block mt-4" htmlFor="userName">
                      <div className="text-sm font-semibold text-primary mb-1">
                        <span>お名前</span>
                        <span className="ml-2 required">必須</span>
                      </div>
                      <input
                        /* eslint-disable-next-line react/jsx-props-no-spreading */
                        {...createUserRegister('userName',
                          makeRegisterOptions('お名前', [
                            { type: FormValidationType.REQUIRED },
                            { type: FormValidationType.MAX_STRING_LENGTH, value: 100 },
                          ])
                        )}
                        id="userName"
                        className="text-field"
                        type="text"
                        maxLength={100}
                      />
                      <div className="mt-1 text-danger text-xs">
                        {createUserErrors.userName?.message}
                      </div>
                      <ValidationErrorMessages
                        {...{
                          fieldErrors: apiClientError?.errors,
                          field: 'userName',
                        }}
                      />
                    </label>

                    <label className="block mt-4" htmlFor="department">
                      <div className="text-sm font-semibold text-primary mb-1">
                        <span>部署名</span>
                      </div>
                      <input
                        /* eslint-disable-next-line react/jsx-props-no-spreading */
                        {...createUserRegister('department',
                          makeRegisterOptions('部署名', [
                            { type: FormValidationType.MAX_STRING_LENGTH, value: 100 },
                          ])
                        )}
                        className="text-field"
                        id="department"
                        type="text"
                        maxLength={100}
                      />
                      <div className="mt-1 text-danger text-xs">
                        {createUserErrors.department?.message}
                      </div>
                      <ValidationErrorMessages
                        {...{
                          fieldErrors: apiClientError?.errors,
                          field: 'department',
                        }}
                      />
                    </label>

                    <label className="block mt-4" htmlFor="email">
                      <div className="text-sm font-semibold text-primary mb-1">
                        <span>メールアドレス</span>
                        <span className="ml-2 required">必須</span>
                      </div>
                      <input
                        /* eslint-disable-next-line react/jsx-props-no-spreading */
                        {...createUserRegister('email',
                          makeRegisterOptions('メールアドレス', [
                            { type: FormValidationType.REQUIRED },
                            { type: FormValidationType.MAX_STRING_LENGTH, value: 86 },
                            { type: FormValidationType.EMAIL, typeName: 'email' },
                          ])
                        )}
                        id="email"
                        className="text-field"
                        type="text"
                        maxLength={86}
                      />
                      <div className="mt-1 text-danger text-xs">
                        {createUserErrors.email?.message}
                      </div>
                      <ValidationErrorMessages
                        {...{
                          fieldErrors: apiClientError?.errors,
                          field: 'email',
                        }}
                      />
                    </label>

                    <label className="block mt-4" htmlFor="employeeNumber">
                      <div className="text-sm font-semibold text-primary mb-1">
                        <span>社員番号</span>
                      </div>
                      <input
                        /* eslint-disable-next-line react/jsx-props-no-spreading */
                        {...createUserRegister('employeeNumber',
                          makeRegisterOptions('郵便番号（ハイフンなし）', [
                            { type: FormValidationType.MAX_STRING_LENGTH, value: 100 },
                          ])
                        )}
                        className="text-field"
                        id="employeeNumber"
                        type="text"
                        maxLength={100}
                      />
                      <div className="mt-1 text-danger text-xs">
                        {createUserErrors.employeeNumber?.message}
                      </div>
                      <ValidationErrorMessages
                        {...{
                          fieldErrors: apiClientError?.errors,
                          field: 'employeeNumber',
                        }}
                      />
                    </label>

                    <label className="block mt-4" htmlFor="companyName">
                      <div className="text-sm font-semibold text-primary mb-1">
                        <span>会社名</span>
                      </div>
                      <input
                        /* eslint-disable-next-line react/jsx-props-no-spreading */
                        {...createUserRegister('companyName',
                          makeRegisterOptions('会社名', [
                            { type: FormValidationType.MAX_STRING_LENGTH, value: 100 },
                          ])
                        )}
                        className="text-field"
                        id="companyName"
                        type="text"
                        maxLength={100}
                      />
                      <div className="mt-1 text-danger text-xs">
                        {createUserErrors.companyName?.message}
                      </div>
                      <ValidationErrorMessages
                        {...{
                          fieldErrors: apiClientError?.errors,
                          field: 'companyName',
                        }}
                      />
                    </label>

                  </div>
                </div>
              </form>
            )}

            <div className="form-modal-footer-container">
              <div className="form-modal-footer">
                <button
                  className="btn-sub"
                  type="button"
                  onClick={() => setShowModal(false)}
                  >
                  キャンセル
                </button>

                { showSalesmanSearch && (
                <button
                  className="btn-primary"
                  type="button"
                  form="searchUserForm"
                  onClick={handleOwnerEmailSubmit}
                  >
                  検 索
                </button>
                )}

                { showSalesmanResult && (
                <button
                  className="float-right btn-primary"
                  type="button"
                  onClick={() => add()}
                  >
                  追 加
                </button>
                )}

                { showSalesmanResultNone && (
                <button
                  className="float-right btn-primary"
                  type="submit"
                  form="createUserForm"
                  >
                  追 加
                </button>
                )}
              </div>
            </div>
          </div>
        </section>
      </ReactModal>
    </>
  );
};